import React, { useState, useEffect, useRef } from 'react';
import { Send, ArrowLeft } from 'lucide-react';
import MessageList from './MessageList';
import chatApi from '../services/chatApi';

const ChatWindow = ({ userId, userName, onBack, onSendMessage, currentUserId }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const messagesContainerRef = useRef(null);

  useEffect(() => {
    loadMessages();
  }, [userId]);

  const loadMessages = async (pageNum = 0) => {
    try {
      setLoading(true);
      const response = await chatApi.getConversation(userId, pageNum, 50);
      const newMessages = response.content || [];
      
      if (pageNum === 0) {
        setMessages(newMessages.reverse());
      } else {
        setMessages(prev => [...newMessages.reverse(), ...prev]);
      }
      
      setHasMore(!response.last);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (inputValue.trim() && onSendMessage) {
      onSendMessage(userId, inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewMessage = React.useCallback((message) => {
    console.log('ChatWindow: Received message event:', message);
    const isRelevant = (message.senderId === userId && message.recipientId === currentUserId) ||
                       (message.recipientId === userId && message.senderId === currentUserId);
    
    if (isRelevant) {
      setMessages(prev => {
        // Sprawdź czy wiadomość już istnieje (porównaj po ID lub treści + timestamp)
        const exists = prev.find(m => {
          if (m.id === message.id) return true;
          // Dla wiadomości optymistycznych sprawdź po treści i czasie
          if (m.content === message.content && 
              Math.abs(new Date(m.createdAt).getTime() - new Date(message.createdAt).getTime()) < 5000) {
            return true;
          }
          return false;
        });
        
        if (!exists) {
          console.log('ChatWindow: Adding new message to list');
          return [...prev, message];
        } else {
          console.log('ChatWindow: Message already exists, skipping');
          return prev;
        }
      });
    } else {
      console.log('ChatWindow: Message not relevant for this conversation');
    }
  }, [userId, currentUserId]);

  useEffect(() => {
    // Nasłuchuj na nowe wiadomości przez custom event
    const handleMessage = (event) => {
      handleNewMessage(event.detail);
    };
    
    window.addEventListener('chat-message', handleMessage);
    return () => {
      window.removeEventListener('chat-message', handleMessage);
    };
  }, [handleNewMessage]);

  const markAsRead = async (messageId) => {
    try {
      await chatApi.markAsRead(messageId);
      setMessages(prev =>
        prev.map(msg =>
          msg.id === messageId ? { ...msg, read: true } : msg
        )
      );
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
        <button
          onClick={onBack}
          className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 ml-3">
          <h3 className="font-semibold text-gray-900">{userName}</h3>
        </div>
      </div>

      {/* Messages */}
      {loading && page === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500 text-sm">Ładowanie wiadomości...</p>
        </div>
      ) : (
        <MessageList messages={messages} onMarkAsRead={markAsRead} />
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex items-end space-x-2">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Napisz wiadomość..."
            rows={1}
            className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            style={{ minHeight: '40px', maxHeight: '120px' }}
          />
          <button
            onClick={handleSend}
            disabled={!inputValue.trim()}
            className="p-2 rounded-md bg-primary-500 text-white hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
